import {
    Component, ViewChild, OnInit, OnDestroy, AfterViewInit, ElementRef, Input,
    TemplateRef, Renderer2, ChangeDetectorRef, Inject, Optional, ViewContainerRef
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Gallery, GalleryConfig, ThumbnailsPosition, GalleryItem, ImageItem, GalleryRef } from '@ngx-gallery/core';
import { CustomizationService, LAYOUT_TYPE, LayoutService, FileService } from '@pepperi/lib';
import { DialogService } from '@pepperi/lib/dialog';
import { pepperiIconArrowRightAlt } from '@pepperi/lib/icon';
import { FormGroup } from '@angular/forms';

import 'hammerjs';

export interface ImagesFilmstripDialogData {
    key: string;
    value: string;
    label: string;
    currIndex: number;
    showThumbnails: boolean;
    objectId: string;
}

export const slideInAnimation = trigger('slideAnimation', [
    state('in', style({ transform: 'translateY(0)', opacity: 1 })),
    transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate(400)
    ]),
    transition(':leave', [
        animate(400, style({ transform: 'translateY(-100%)', opacity: 0 }))
    ])
]);

@Component({
    selector: 'pep-images-filmstrip',
    templateUrl: './images-filmstrip.component.html',
    styleUrls: ['./images-filmstrip.component.scss'],
    animations: [slideInAnimation]
})
export class PepperiImagesFilmstripComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('arrowRightTemplate') arrowRightTemplate: TemplateRef<any>;
    // @ViewChild('ngxViewerImage') ngxViewerImage: any; // TODO: Check if we need to use this??

    @Input() value = '';
    @Input() key = '';
    @Input() label = '';
    @Input() xAlignment = '0';
    @Input() rowSpan = 1;

    controlType = 'images';

    @Input() form: FormGroup = null;
    @Input() objectId = null;
    @Input() showTitle = false;
    @Input() layoutType: LAYOUT_TYPE.PepperiForm;
    @Input() currIndex = 0;
    @Input() showThumbnails = false;
    @Input() items: GalleryItem[];

    @ViewChild('galleryCont') galleryCont: ElementRef;
    @ViewChild('galleryDialogTemplate', { read: TemplateRef }) galleryDialogTemplate: TemplateRef<any>;
    @ViewChild('galleryDialogCont') galleryDialogCont: ElementRef;

    config: GalleryConfig;

    required = false;
    readonly = false;
    disabled = false;
    fieldHeight = '';
    standAlone = false;

    galleryWidth = 0;
    galleryHeight;
    isVertical = false;
    galleryRef: GalleryRef;
    dialogGalleryRef: GalleryRef;

    constructor(
        private customizationService: CustomizationService,
        private dialogService: DialogService,
        private layoutService: LayoutService,
        private fileService: FileService,
        private gallery: Gallery,
        private renderer: Renderer2,
        private element: ElementRef,
        private cd: ChangeDetectorRef
    ) {
        this.config = {
            // loadingMode: 'indeterminate',
            autoPlay: false,
            imageSize: 'contain',
            loadingStrategy: 'lazy',
            gestures: true, // Use touch events (requires hammer.js)
            loop: true,
            dots: false,
            nav: false, // Show navigation buttons
            counter: false // Show counter
        };
    }

    ngOnInit(): void {
        if (this.form === null) {
            this.standAlone = true;
            this.form = this.customizationService.getDefaultFromGroup(this.key, this.value, this.required, this.readonly, this.disabled);

            this.renderer.addClass(this.element.nativeElement, CustomizationService.STAND_ALONE_FIELD_CLASS_NAME);
        }

        this.fieldHeight = this.customizationService.calculateFieldHeight(this.layoutType, this.rowSpan, this.standAlone);

        const imgArr = this.value ? this.value.split(';') : [];
        // add No image image when there is no images
        if (imgArr.length === 0) {
            const noImageSrc = this.fileService.getNoImagePath();
            imgArr.push(noImageSrc);
        }

        this.items = imgArr.map(img => new ImageItem({ src: img, thumb: img }));
        this.config.dots = this.config.nav = this.items.length > 1;
        this.config.thumb = (this.key === 'ItemFilmstripImages' || this.showThumbnails) && this.items.length > 1 ? true : false;
        this.config.navIcon = pepperiIconArrowRightAlt.data;
        this.currIndex =
            this.key === 'ItemFilmstripImages' &&
                this.currIndex === 0 &&
                this.items.length > 1 ?
                Math.floor(this.items.length / 2) - 1 : this.currIndex;

        this.galleryRef = this.gallery.ref(this.key + '-' + this.objectId + '-gallery');
    }

    ngAfterViewInit(): void {
        this.initGalleryStyle(this.galleryCont, this.galleryRef);
    }

    ngOnDestroy(): void {
        if (this.galleryRef) { this.galleryRef.destroy(); }
        if (this.dialogGalleryRef) { this.dialogGalleryRef.destroy(); }
    }

    initGalleryStyle(galleryContainer, galleryRef): void {
        // Set thumbnails position and sliding direction
        this.setThumbnailDimension(galleryContainer);

        galleryRef.setConfig(this.config);
        galleryRef.load(this.items);

        const currentIndex = this.currIndex;
        if (currentIndex > 0) {
            galleryRef.set(currentIndex);
        }
    }

    indexChange(item: any): void {
        this.currIndex = item.currIndex;
        // if (this.ngxViewerImage && this.ngxViewerImage.scale) {
        //     this.ngxViewerImage.scale = 1;
        //     this.updateStyle();
        // }
    }

    onError(e): void {
        const noImageSrc = this.fileService.getNoImagePath();
        const noimg = new ImageItem({ src: noImageSrc, thumb: noImageSrc });
        this.items.splice(e.itemIndex, 1, noimg);
    }

    enlargeImage(event: any): void {
        const dialogRef = this.dialogService.openDialog(this.galleryDialogTemplate);

        dialogRef.afterOpened().subscribe(() => {
            this.afterDialogOpened();
        });

        // Update currentIndex in galleryRef
        dialogRef.afterClosed().subscribe(value => {
            this.galleryRef.set(this.currIndex);
        });
    }

    afterDialogOpened(): void {
        this.dialogGalleryRef = this.gallery.ref(this.key + '-' + this.objectId + '-dlgGallery');
        this.initGalleryStyle(this.galleryDialogCont, this.dialogGalleryRef);
    }

    setThumbnailDimension(galleryContainer): void {
        this.galleryWidth = galleryContainer.nativeElement.clientWidth;
        this.galleryHeight = galleryContainer.nativeElement.clientHeight;

        this.isVertical = this.galleryWidth < this.galleryHeight;

        if (this.isVertical) {
            let width = (this.galleryWidth - ((this.items.length - 1) * 16)) / this.items.length;
            width = Math.min(Math.max(width, 24), 96);

            this.config.thumbHeight = width;
            this.config.thumbWidth = (width + 16);
        } else {
            let height = (this.galleryHeight - ((this.items.length - 1) * 16)) / this.items.length;
            height = Math.min(Math.max(height, 24), 96);

            this.config.thumbHeight = (height + 16);
            this.config.thumbWidth = height;
        }

        this.config.thumbPosition = this.isVertical ?
            ThumbnailsPosition.Bottom : this.layoutService.isRtl() ?
                ThumbnailsPosition.Right : ThumbnailsPosition.Left;
        this.config.slidingDirection = this.key !== 'ItemFilmstripImages' ? 'horizontal' : this.isVertical ? 'horizontal' : 'vertical';
    }

    // scrollFunction(e: any): boolean {
    //     e.deltaY > 0 ? this.zoomOut() : this.zoomIn();
    //     return false;
    // }

    // zoomIn(): void {
    //     if (this.ngxViewerImage.scale < 2) {
    //         this.ngxViewerImage.scale *= (1 + this.ngxViewerImage.config.zoomFactor);
    //     } else {
    //         this.ngxViewerImage.scale = 2;
    //     }
    //     this.updateStyle();
    // }

    // zoomOut(): void {
    //     if (this.ngxViewerImage.scale > this.ngxViewerImage.config.zoomFactor) {
    //         this.ngxViewerImage.scale /= (1 + this.ngxViewerImage.config.zoomFactor);
    //     }
    //     this.updateStyle();
    // }

    // private updateStyle(): void {
    //     this.ngxViewerImage.style.transform = `translate(${this.ngxViewerImage.translateX}px,
    //         ${this.ngxViewerImage.translateY}px) rotate(${this.ngxViewerImage.rotation}deg) scale(${this.ngxViewerImage.scale})`;

    //     this.cd.detectChanges();
    // }
}
